import { useState } from "react";

import styles from "./AccessPopover.module.css";

interface RowActionsProps {
  /** "section" | "page" — drives the labels. */
  kind: "section" | "page";
  title: string;
  /** True while any structural mutation is in flight. */
  busy: boolean;
  /** Extra caution line shown with the delete confirm (e.g. published warning). */
  deleteHint: string;
  /** Sections rename here; pages don't (their title lives on the version). */
  onRename?: (title: string) => Promise<boolean>;
  onDelete: () => Promise<boolean>;
  /** Called after a successful action so the owning popover can close. */
  onDone: () => void;
}

/**
 * The structural actions in a sidebar row's manage popover: rename (sections)
 * and a two-step delete. Deliberately small — the server is the authority on
 * what's deletable (a non-empty section 409s and the message surfaces via
 * the hook's opError), so this never pre-judges beyond the confirm step.
 */
export function RowActions({
  kind,
  title,
  busy,
  deleteHint,
  onRename,
  onDelete,
  onDone,
}: RowActionsProps) {
  const [mode, setMode] = useState<"idle" | "renaming" | "confirmingDelete">("idle");
  const [draftTitle, setDraftTitle] = useState(title);

  const rename = async () => {
    if (!onRename) return;
    if (await onRename(draftTitle)) onDone();
  };

  const remove = async () => {
    if (await onDelete()) onDone();
  };

  if (mode === "renaming") {
    return (
      <>
        <input
          type="text"
          value={draftTitle}
          className={styles.actionInput}
          aria-label={`New title for ${title}`}
          autoFocus
          disabled={busy}
          onChange={(e) => setDraftTitle(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") void rename();
            if (e.key === "Escape") {
              e.stopPropagation();
              setMode("idle");
            }
          }}
        />
        <div className={styles.actionRow}>
          <button type="button" className={styles.actionBtn} disabled={busy} onClick={rename}>
            Save
          </button>
          <button
            type="button"
            className={styles.actionBtn}
            disabled={busy}
            onClick={() => setMode("idle")}
          >
            Cancel
          </button>
        </div>
      </>
    );
  }

  if (mode === "confirmingDelete") {
    return (
      <>
        <p className={styles.confirmHint}>{deleteHint}</p>
        <div className={styles.actionRow}>
          <button
            type="button"
            className={`${styles.actionBtn} ${styles.actionDanger}`}
            disabled={busy}
            onClick={remove}
          >
            Delete {kind}
          </button>
          <button
            type="button"
            className={styles.actionBtn}
            disabled={busy}
            onClick={() => setMode("idle")}
          >
            Keep it
          </button>
        </div>
      </>
    );
  }

  return (
    <>
      {onRename && (
        <button
          type="button"
          className={styles.actionBtn}
          disabled={busy}
          onClick={() => {
            setDraftTitle(title);
            setMode("renaming");
          }}
        >
          Rename…
        </button>
      )}
      <button
        type="button"
        className={`${styles.actionBtn} ${styles.actionDanger}`}
        disabled={busy}
        onClick={() => setMode("confirmingDelete")}
      >
        Delete {kind}…
      </button>
    </>
  );
}
